import * as stylex from '@stylexjs/stylex'
import { type ElementType, type ForwardedRef } from 'react'
import { Box } from '../Box/Box'
import { ChipBase, type ChipBaseProps } from '../ChipBase/ChipBase'
import { IconButton, type IconButtonProps } from '../IconButton/IconButton'
import { CloseSmall } from '../Icons'
import { type SvgIconProps } from '../SvgIcon/SvgIcon'
import { Text } from '../Text/Text'
import { colors } from '../tokens/colors.stylex'
import { typography } from '../tokens/typography.stylex'
import { useXStyles, type XStyles } from '../useXStyles/useXStyles'

export const sizeVariants = stylex.create({
  medium: {
    padding: '4px 8px',
    fontSize: typography.sizeBody,
    lineHeight: typography.lineHeightBody,
  },
  small: {
    padding: '2px 6px',
    fontSize: typography.sizeCaption,
    lineHeight: typography.lineHeightCaption,
  },
  xSmall: {
    padding: '2px 4px',
    fontSize: typography.sizeTiny,
    lineHeight: typography.lineHeightTiny,
  },
})

/* 文字色統一走 700 階（UIUX 2026-09-02）；violet 目前沒有 700 token，暫留 600 */
const colorVariants = stylex.create({
  default: {
    backgroundColor: colors.grey100,
    color: colors.grey700,
  },
  primary: {
    backgroundColor: colors.primary100,
    color: colors.primary700,
  },
  blue: {
    backgroundColor: colors.blue100,
    color: colors.blue700,
  },
  green: {
    backgroundColor: colors.green100,
    color: colors.green700,
  },
  red: {
    backgroundColor: colors.red100,
    color: colors.red700,
  },
  blueGrey: {
    backgroundColor: colors.blueGrey100,
    color: colors.blueGrey700,
  },
  violet: {
    backgroundColor: colors.violet100,
    color: colors.violet600,
  },
})

const selectedVariants = stylex.create({
  default: {
    borderColor: colors.grey600,
    borderStyle: 'solid',
    borderWidth: '1px',
    backgroundColor: colors.grey100,
  },
  primary: {
    borderColor: colors.primary600,
    borderStyle: 'solid',
    borderWidth: '1px',
    backgroundColor: colors.primary100,
    color: colors.primary700,
  },
  blue: {
    borderColor: colors.blue600,
    borderStyle: 'solid',
    borderWidth: '1px',
    backgroundColor: colors.blue100,
  },
  green: {
    borderColor: colors.green600,
    borderStyle: 'solid',
    borderWidth: '1px',
    backgroundColor: colors.green100,
  },
  red: {
    borderColor: colors.red600,
    borderStyle: 'solid',
    borderWidth: '1px',
    backgroundColor: colors.red100,
  },
  blueGrey: {
    borderColor: colors.blueGrey600,
    borderStyle: 'solid',
    borderWidth: '1px',
    backgroundColor: colors.blueGrey100,
  },
  violet: {
    borderColor: colors.violet600,
    borderStyle: 'solid',
    borderWidth: '1px',
    backgroundColor: colors.violet100,
  },
})

const chipVariants = stylex.create({
  rounded: {
    borderRadius: 64,
    overflow: 'hidden',
  },
  square: {
    borderRadius: 4,
  },
})

const _baseStyles = stylex.create({
  selectable: {
    borderColor: 'transparent',
    borderStyle: 'solid',
    borderWidth: '1px',
    cursor: 'pointer',
    userSelect: 'none',
  },
  disabled: {
    backgroundColor: colors.grey50,
    color: colors.grey300,
    pointerEvents: 'none',
  },
  outlined: {
    borderColor: 'currentColor',
    borderStyle: 'solid',
    borderWidth: '1px',
  },
  transparentBackground: {
    backgroundColor: 'transparent',
  },
  labelText: {
    fontSize: 'inherit',
    lineHeight: 'inherit',
  },
  actionIcon: {
    color: 'inherit',
    fontSize: '1.4em',
    marginLeft: 4,
  },
  startIcon: {
    color: 'inherit',
    fontSize: '1.4em',
    marginRight: 4,
  },
  iconButton: {
    color: 'inherit',
  },
  body: {},
})

const prefixStyles = stylex.create({
  prefix: (color: string) => ({
    alignItems: 'center',
    backgroundColor: color,
    clipPath: 'polygon(0 0, 110% -110%, 90% 105%, 0% 110%)',
    color: 'white',
    display: 'flex',
    height: '100%',
    paddingLeft: 6,
    paddingRight: 8,
  }),
})

export type ChipSize = 'xSmall' | 'small' | 'medium'
export type ChipColor = 'default' | 'primary' | 'blue' | 'green' | 'red' | 'blueGrey' | 'violet'
export type ChipVariant = 'rounded' | 'square'

export type ChipProps<RootComponent extends ElementType = 'div', BoxElement extends HTMLElement = HTMLDivElement> = {
  /** 根元素的 ref */
  rootRef?: ForwardedRef<HTMLDivElement>
  /** 根元素的自訂樣式 */
  rootStyle?: stylex.StyleXStyles
  /**
   * 外觀變體：rounded 為圓角膠囊、square 為方角
   * @default 'rounded'
   */
  variant?: ChipVariant
  /** 是否顯示外框線 */
  isOutlined?: boolean
  /**
   * 尺寸
   * @default 'medium'
   */
  size?: ChipSize
  /**
   * 顏色主題
   * @default 'default'
   */
  color?: ChipColor
  /** 是否為選取狀態 */
  selected?: boolean
  /** 是否為柔和背景（搭配 isOutlined 使用，保留背景色） */
  isSoft?: boolean
  /** 是否禁用 */
  disabled?: boolean
  /** 刪除按鈕點擊回呼；傳入即顯示刪除圖示 */
  onDelete?: IconButtonProps['onClick']
  /** 起始圖示元件 */
  StartIcon?: ElementType<Omit<SvgIconProps, 'children'>>
  /** 動作圖示元件（顯示於文字後方） */
  ActionIcon?: ElementType<Omit<SvgIconProps, 'children'>>
  /** 動作圖示點擊回呼 */
  onActionClick?: IconButtonProps['onClick']
  /** 起始圖示點擊回呼 */
  onStartClick?: IconButtonProps['onClick']
  /** 覆寫內部子樣式 */
  xStyles?: Partial<XStyles<keyof typeof _baseStyles>>
  /** 傳遞給刪除 IconButton 的額外 props */
  DeleteIconButtonProps?: IconButtonProps
  /** 前綴標籤文字（以色塊呈現於左側） */
  prefix?: string
} & ChipBaseProps<RootComponent, BoxElement>

/**
 * Chip 用於顯示標籤、篩選條件或可選取/可刪除的小型互動元素。
 * 支援 rounded / square 兩種外觀、多種顏色與尺寸，並可附加前綴、圖示及刪除按鈕。
 */
export const Chip = <RootComponent extends ElementType = 'div', BoxElement extends HTMLElement = HTMLDivElement>(props: ChipProps<RootComponent, BoxElement>) => {
  const {
    rootStyle,
    xStyles,
    component = 'div',
    variant = 'rounded',
    size = 'medium',
    children,
    color = 'default',
    selected,
    disabled,
    isOutlined,
    isSoft,
    onClick,
    onDelete,
    onActionClick,
    onStartClick,
    ActionIcon,
    StartIcon,
    DeleteIconButtonProps,
    prefix,
    ...rest
  } = props
  const selectable = Boolean(onClick)
  const deletable = Boolean(onDelete)
  const tabIndex = selectable ? 0 : undefined
  const shouldBackgroundTransparent = isOutlined && !isSoft
  const baseStyles = useXStyles(_baseStyles, xStyles)

  return (
    <ChipBase
      component={component}
      tabIndex={tabIndex}
      onClick={onClick}
      rootStyle={[
        colorVariants[color],
        chipVariants[variant],
        selectable && baseStyles.selectable,
        isOutlined && baseStyles.outlined,
        disabled && baseStyles.disabled,
        shouldBackgroundTransparent && baseStyles.transparentBackground,
        selected && selectedVariants[color],
        rootStyle,
      ]}
      {...rest}
    >
      {prefix && (
        <Box rootStyle={[prefixStyles.prefix(color), sizeVariants[size]]}>
          <Text rootStyle={baseStyles.labelText} component="span">
            {prefix}
          </Text>
        </Box>
      )}
      <Box rootStyle={[sizeVariants[size], baseStyles.body]}>
        {StartIcon && (
          <IconButton
            variant="text"
            onClick={onStartClick}
            rootStyle={baseStyles.iconButton}
          >
            <StartIcon rootStyle={[baseStyles.startIcon]} />
          </IconButton>
        )}
        <Text rootStyle={baseStyles.labelText} component="span">{children}</Text>
        {deletable && (
          <IconButton
            variant="text"
            onClick={(event) => {
              event.stopPropagation()
              onDelete?.(event)
            }}
            rootStyle={[baseStyles.iconButton, DeleteIconButtonProps?.rootStyle]}
            {...DeleteIconButtonProps}
          >
            <CloseSmall rootStyle={[baseStyles.actionIcon]} />
          </IconButton>
        )}
        {ActionIcon && (
          <IconButton
            variant="text"
            onClick={onActionClick}
            rootStyle={baseStyles.iconButton}
          >
            <ActionIcon rootStyle={[baseStyles.actionIcon]} />
          </IconButton>
        )}
      </Box>
    </ChipBase>
  )
}
